import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ApiCallsService } from '../../services/api-calls.service';
import { AgGridTableService } from '../../services/ag-grid.service';
import { ToastService } from '../../services/toast.service';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface DropdownItem {
  site_id: string;
  site_name: string;
  selected: boolean;
}

export interface TowerItem {
  id: string;
  tower_name: string;
  selected: boolean;
}

export interface TypeOption {
  label: string;
  value: string;
}

/** Emitted when multiple sites are selected */
export interface MultiSiteFilterOutput {
  siteIds: string[];
  type: string;
}

/** Emitted when a single site + towers are selected */
export interface SingleSiteTowerFilterOutput {
  siteId: string|null;
  towerIds: string[];
  type: string;
}

export type FilterOutput = MultiSiteFilterOutput | SingleSiteTowerFilterOutput;

// ─── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-group-action-selector',
  imports: [CommonModule, NgbModule, FormsModule],
  templateUrl: './group-action-selector.component.html',
  styleUrl: './group-action-selector.component.scss',
})
export class GroupActionSelectorComponent implements OnInit {

  // ── Inputs ──────────────────────────────────────────────────────────────────
  @Input() showSiteDropdown: boolean = true;
  @Input() header: string = '';

  // ── Output ──────────────────────────────────────────────────────────────────
  @Output() filterApplied = new EventEmitter<FilterOutput>();
  @Output() showTable = new EventEmitter<void>();
  
  // ── Site state ──────────────────────────────────────────────────────────────
  siteid: string | null = '';
  lockedSiteName: string = '';
  siteloading: boolean = false;
  searchTerm: string = '';
  basicdropdownitem: DropdownItem[] = [];
  shimmerArray = Array(10);
toggleTowerSelection(tower: any, event?: Event) {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }

  tower.selected = !tower.selected;
  this.onTowerSelectionChange(tower);
}
  // ── Type dropdown state ──────────────────────────────────────────────────────
 @Input() typeOptions: TypeOption[] = [
  ];
  selectedType: TypeOption = this.typeOptions[0]||[]; // default: Overload

  // ── Tower state (single-site mode) ──────────────────────────────────────────
  showTowerDropdown: boolean = false;
  towerLoading: boolean = false;
  towerItems: TowerItem[] = [];
  towerSearchTerm: string = '';

  // ── Misc ────────────────────────────────────────────────────────────────────
  private readonly dropdownButtonText = 'Selected Sites';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly apiservice: ApiCallsService,
    private readonly aggrid: AgGridTableService,
    private readonly toast: ToastService   // ✅ add this

  ) {}

  // ── Lifecycle ────────────────────────────────────────────────────────────────
isSingleSite:boolean = false
  ngOnInit(): void {
    const encodedSiteId =
      this.route.snapshot.queryParamMap.get('siteid') ||
      localStorage.getItem('site_idLogin');

    this.siteid = encodedSiteId ? atob(encodedSiteId) : null;

    const isSingleSite = this.siteid && this.siteid !== 'ALL';

    if (isSingleSite) {
      this.isSingleSite = true
      this.lockedSiteName = this.resolveLockedSiteName(this.siteid ?? '');
      this.handleSingleSiteSelection(this.siteid)
    }
    else{
      this.getsitelist()
    }

  }

  // ── Site helpers ─────────────────────────────────────────────────────────────

  private resolveLockedSiteName(siteId: string): string {
    try {
      const storedSites = localStorage.getItem('siteList');
      if (!storedSites) return 'Selected Site';
      const parsed = JSON.parse(storedSites);
      const siteList = Array.isArray(parsed) ? parsed : parsed?.Data?.list ?? [];
      return siteList.find((s: any) => s.site_id === siteId)?.site_name ?? 'Selected Site';
    } catch {
      return 'Selected Site';
    }
  }

  get filteredItems(): DropdownItem[] {
    const selectAllItem = this.basicdropdownitem.find(i => i.site_name === 'Select All Sites');
    let displayable = this.basicdropdownitem.filter(i => i.site_name !== 'Select All Sites');

    const term = this.searchTerm.trim().toLowerCase();
    if (term) {
      displayable = displayable.filter(i => i.site_name.toLowerCase().includes(term));
    }

    displayable.sort((a, b) => {
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;
      return 0;
    });

    return selectAllItem ? [selectAllItem, ...displayable] : displayable;
  }

  get selectedItemsCount(): number {
    return this.basicdropdownitem.filter(
      i => i.selected && i.site_name !== 'Select All Sites'
    ).length;
  }

  getSelectedSiteNames(): string {
    const count = this.selectedItemsCount;
    if (count === 0) return this.dropdownButtonText;
    const names = this.basicdropdownitem
      .filter(i => i.selected && i.site_name !== 'Select All Sites')
      .map(i => i.site_name);
    return count <= 2 ? names.join(', ') : `${names[0]}, +${count - 1} more`;
  }

  getsitelist(): void {
    this.siteloading = true;
    const storedData = localStorage.getItem('siteList');
    this.apiservice.getSiteList().subscribe(data => {
      const siteListWithSelected: DropdownItem[] = data.Data.list.map((site: any) => ({
        ...site,
        selected: false,
      }));
      if (storedData !== '') {
        localStorage.setItem('siteList', JSON.stringify(data.Data.list));
      }
      this.basicdropdownitem = [
        { site_name: 'Select All Sites', site_id: 'all', selected: false },
        ...siteListWithSelected,
      ];
      this.updateSelectAllState();
      this.siteloading = false;
    });
  }

  updateSelectAllState(): void {
    const selectAll = this.basicdropdownitem.find(i => i.site_name === 'Select All Sites');
    if (!selectAll) return;
    const allReal = this.basicdropdownitem.filter(i => i.site_name !== 'Select All Sites');
    selectAll.selected = allReal.length > 0 && allReal.every(i => i.selected);
  }

  onItemSelectionChange(changedItem: DropdownItem): void {
    this.searchTerm = '';

    if (changedItem.site_name === 'Select All Sites') {
      const isSelected = changedItem.selected;
      this.basicdropdownitem
        .filter(i => i.site_name !== 'Select All Sites')
        .forEach(i => (i.selected = isSelected));
    }

    this.updateSelectAllState();
    this.handleSingleSiteSelection();
  }

  /**
   * When exactly one site is selected, fetch tower data and show the tower dropdown.
   * When more than one site is selected, hide the tower dropdown.
   */
  private handleSingleSiteSelection(siteid:string|null =''): void {
    if (siteid) {
      this.fetchTowers(siteid)
      return
    }
    const selectedSites = this.basicdropdownitem.filter(
      i => i.selected && i.site_name !== 'Select All Sites'
    );
    if (selectedSites.length === 1) {
      this.fetchTowers(selectedSites[0].site_id);
    } else {
      // Multi-site or none: hide tower dropdown
      this.showTowerDropdown = false;
      this.towerItems = [];
      this.towerSearchTerm = '';
    }
  }

  shouldShowNoSitesFound(): boolean {
    if (!this.basicdropdownitem?.length) return false;
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return false;
    return !this.basicdropdownitem
      .filter(i => i.site_name !== 'Select All Sites')
      .some(i => i.site_name.toLowerCase().includes(term));
  }

  shouldShowNoSitesAvailable(): boolean {
    if (!this.basicdropdownitem) return true;
    return !this.searchTerm &&
      this.basicdropdownitem.filter(i => i.site_name !== 'Select All Sites').length === 0;
  }

  // ── Type helpers ─────────────────────────────────────────────────────────────

  onTypeChange(type: TypeOption): void {
    this.selectedType = type;
  }

  // ── Tower helpers ─────────────────────────────────────────────────────────────

  private fetchTowers(siteId: string): void {
    this.towerLoading = true;
    this.showTowerDropdown = true;
    this.towerItems = [];
    this.towerSearchTerm = '';

    // Replace with your actual API method and response shape
    this.apiservice.towerlist(siteId).subscribe({
      next: (data: any) => {
        const rawList: any[] = data?.Data?.list ?? data?.Data ?? [];
        this.towerItems = [
          { id: 'all', tower_name: 'Select All Towers', selected: false },
          ...rawList.map((t: any) => ({
            id: t.id,
            tower_name: t.name,
            selected: false,
          })),
        ];
        this.towerLoading = false;
      },
      error: () => {
        this.towerItems = [];
        this.towerLoading = false;
      },
    });
  }

  get filteredTowers(): TowerItem[] {
    const term = this.towerSearchTerm.trim().toLowerCase();
    let list = this.towerItems.filter(t => t.tower_name !== 'Select All Towers');

    if (term) {
      list = list.filter(t => t.tower_name.toLowerCase().includes(term));
    }

    list.sort((a, b) => {
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;
      return 0;
    });

    const selectAll = this.towerItems.find(t => t.tower_name === 'Select All Towers');
    return selectAll ? [selectAll, ...list] : list;
  }

  get selectedTowersCount(): number {
    return this.towerItems.filter(
      t => t.selected && t.tower_name !== 'Select All Towers'
    ).length;
  }

  getSelectedTowerNames(): string {
    const count = this.selectedTowersCount;
    if (count === 0) return 'Select Towers';
    const names = this.towerItems
      .filter(t => t.selected && t.tower_name !== 'Select All Towers')
      .map(t => t.tower_name);
    return count <= 2 ? names.join(', ') : `${names[0]}, +${count - 1} more`;
  }

  onTowerSelectionChange(changedTower: TowerItem): void {
    if (changedTower.tower_name === 'Select All Towers') {
      const isSelected = changedTower.selected;
      this.towerItems
        .filter(t => t.tower_name !== 'Select All Towers')
        .forEach(t => (t.selected = isSelected));
    }
    this.updateSelectAllTowerState();
  }

  private updateSelectAllTowerState(): void {
    const selectAll = this.towerItems.find(t => t.tower_name === 'Select All Towers');
    if (!selectAll) return;
    const allReal = this.towerItems.filter(t => t.tower_name !== 'Select All Towers');
    selectAll.selected = allReal.length > 0 && allReal.every(t => t.selected);
  }

  // ── Apply / Reset ─────────────────────────────────────────────────────────────

onApply(): void {
  const selectedSites = this.basicdropdownitem.filter(
    i => i.selected && i.site_name !== 'Select All Sites'
  );

  // ✅ FIXED LOGIC
  if (selectedSites.length === 0 && !this.isSingleSite) {
    this.toast.showToast(
      'Please select at least one site',
      'colored',
      3000,
      'warning'
    );
    return;
  }

  if (selectedSites.length > 1) {
    const output: MultiSiteFilterOutput = {
      siteIds: selectedSites.map(s => s.site_id),
      type: this.selectedType.value,
    };

    this.filterApplied.emit(output);

  } else {
    const selectedTowers = this.towerItems.filter(
      t => t.selected && t.tower_name !== 'Select All Towers'
    );

    const output: SingleSiteTowerFilterOutput = {
      siteId: selectedSites[0]?.site_id || this.siteid,
      towerIds: selectedTowers.map(t => t.id),
      type: this.selectedType.value,
    };

    this.filterApplied.emit(output);
  }
}
  onReset(): void {
    // Deselect all sites
    this.basicdropdownitem.forEach(i => (i.selected = false));
    this.searchTerm = '';

    // Reset towers
    this.towerItems = [];
    this.towerSearchTerm = '';
    this.showTowerDropdown = false;
    this.showTable.emit();

    // Reset type to default
    this.selectedType = this.typeOptions[0];
  }

  // ── Shared helpers ────────────────────────────────────────────────────────────

  handleItemClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}